import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'Contact' },
  ];

  // Dynamic classes based on scroll state and page
  const headerClasses = `
    fixed top-0 left-0 right-0 z-50 transition-all duration-500
    ${isScrolled || !isHomePage
      ? 'bg-primary-warm shadow-soft backdrop-blur-sm'
      : 'bg-transparent'}
  `;

  const logoClasses = `
    text-2xl md:text-3xl font-heading font-bold transition-colors duration-500
    ${isScrolled || !isHomePage ? 'text-secondary' : 'text-white'}
  `;

  const navLinkClasses = ({ isActive }) => `
    text-base font-body font-medium tracking-wide transition-all duration-400
    hover:text-accent-secondary relative
    ${isScrolled || !isHomePage
      ? 'text-text-primary'
      : 'text-white'}
    ${isActive
      ? 'text-accent font-semibold after:absolute after:bottom-[-4px] after:left-0 after:right-0 after:h-[2px] after:bg-accent'
      : ''}
  `;

  const mobileNavLinkClasses = ({ isActive }) => `
    text-lg font-body font-medium tracking-wide transition-colors duration-300
    text-text-primary hover:text-accent
    ${isActive ? 'text-accent font-semibold' : ''}
  `;

  const menuButtonClasses = `
    md:hidden transition-colors duration-500
    ${isScrolled || !isHomePage ? 'text-secondary' : 'text-white'}
    hover:text-accent-secondary
  `;

  return (
    <>
      <header className={headerClasses}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-5 md:py-6">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <h1 className={logoClasses}>
                <span className="font-heading">Ky Tracy</span>
              </h1>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-10">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={navLinkClasses}
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className={menuButtonClasses}
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        >
          <nav
            className="fixed top-0 right-0 h-full w-3/4 max-w-sm bg-white shadow-2xl z-50 p-8 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-6 right-6 text-secondary hover:text-accent-secondary transition-colors"
              aria-label="Close menu"
            >
              <FaTimes size={28} />
            </button>

            {/* Mobile menu header */}
            <div className="mb-12 mt-4">
              <h2 className="text-2xl font-heading font-bold text-secondary">
                Menu
              </h2>
            </div>

            {/* Mobile Navigation Links */}
            <div className="flex flex-col space-y-6">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={mobileNavLinkClasses}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </NavLink>
              ))}
            </div>

            {/* Mobile menu footer */}
            <div className="absolute bottom-8 left-8 right-8">
              <p className="text-sm text-text-secondary">
                Ky Tracy
              </p>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
